import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { WORKSPACES, resolveActive } from "../../config/workspaces";

/**
 * WorkspaceTabsBar — sits directly under the AdminShell topbar. When the
 * user is inside a workspace with 2+ items, we surface every item as a
 * horizontally-scrollable tab so lateral navigation inside the workspace
 * takes ONE click instead of clicking through the sidebar accordion.
 *
 * The bar hides itself on workspaces with 0-1 items so it never adds
 * visual noise on singleton pages.
 */

export default function WorkspaceTabsBar({ isAdmin = false }) {
  const location = useLocation();
  const { workspace } = resolveActive(location.pathname);
  if (!workspace) return null;

  const items = (workspace.items || []).filter((it) => !it.adminOnly || isAdmin);
  if (items.length < 2) return null;

  const Icon = workspace.icon;

  return (
    <div
      className="sticky top-0 z-30 w-full border-b border-neutral-900"
      style={{ background: "rgba(10, 10, 10, 0.92)", backdropFilter: "blur(8px)" }}
      data-testid={`workspace-tabs-${workspace.id}`}
    >
      <div className="flex items-center gap-3 px-4 lg:px-6 py-2 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 shrink-0 pr-3 border-r border-neutral-900">
          <Icon size={14} weight="fill" className="text-red-500" />
          <span className="text-[11px] font-display font-black tracking-wider text-white uppercase">
            {workspace.label}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {items.map((it) => {
            const ItemIcon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={!!it.exact}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-[12px] font-medium whitespace-nowrap transition-all border ${
                    isActive
                      ? "bg-red-600 text-white border-red-500"
                      : "bg-neutral-950 text-neutral-400 border-neutral-900 hover:text-white hover:border-neutral-700"
                  }`
                }
                data-testid={`workspace-tab-${it.testid || it.to.replaceAll("/", "-")}`}
                title={it.label}
              >
                <ItemIcon size={12} weight="bold" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}
